/* eslint-disable jsx-a11y/anchor-is-valid */
/**
 * AssignVehicleView — Available Vehicles map/list/grid with GPS monitoring UX.
 */

import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {KTSVG} from '../../_metronic/helpers/components/KTSVG'
import '../style/AssignVehiclestyle.css'
import '../style/VehicleMonitoringStyle.css'
import SplitPane, {Pane} from 'split-pane-react'
import 'split-pane-react/esm/themes/default.css'
import {MapComponent} from '../UI/Map/MapComponent'
import {useDataLayerValue} from '../../DataLayer'
import {
  ActiveFilterChip,
  AdvanceSearch,
  filterInitials,
  FilterOptions,
} from '../UI/AdvanceSearch/AssignVehicle'
import {
  AvailableVehicleList,
  AvailableVehicleListItem,
} from '../UI/AssignVehicle/AvailableVehicleList'
import {AvailableVehicleDetailPanel} from '../UI/AssignVehicle/AvailableVehicleDetailPanel'
import {
  AvailableVehicleGrid,
  SortDir,
  SortKey,
} from '../UI/AssignVehicle/AvailableVehicleGrid'
import {Loading} from '../../utils/loading'
import {useDispatch, useSelector} from 'react-redux'
import {
  useAssignVehicleQuery,
  useAvailableVehicleStatisticsQuery,
} from '../redux/network/AssignVehicle'
import {
  resetState,
  setGPSVehiclesDetails,
  setMapViewVehicle,
  setMarkers,
} from '../redux/MapDataSlice'
import {useFetchSIDTokenQuery, useGetGPSVehicleDetailsQuery} from '../redux/network/Wialon'
import {skipToken} from '@reduxjs/toolkit/query'
import type {GpsStatus} from '../UI/VehicleMonitoring/VehicleList'

type Props = {
  allJobs: any
  fetchAllJobs: () => void
}

function resolveGpsStatus(gpsItem: any): GpsStatus {
  if (!gpsItem?.pos || gpsItem.pos.x == null || gpsItem.pos.y == null) return 'no_gps'
  if (gpsItem.pos.s > 0) return 'moving'
  if (gpsItem.pos.s === 0) return 'stopped'
  return 'no_gps'
}

const AssignVehicleView: React.FC<Props> = () => {
  const [{company}] = useDataLayerValue()
  const {uniqueDataRedux, MapViewVehicle, ActivePage, GPSVehiclesDetails} = useSelector(
    (state: any) => {
      return {
        uniqueDataRedux: state.MapData?.uniqueDataRedux || [],
        MapViewVehicle: state.MapData?.MapViewVehicle || [],
        ActivePage: state.ActivePage?.ActivePage || '',
        GPSVehiclesDetails: state.MapData?.GPSVehiclesDetails || [],
      }
    }
  )

  const [sizes, setSizes] = useState<(number | string)[]>([280, 'auto'])
  const [filterOptions, setFilterOptions] = useState<FilterOptions>(filterInitials)
  const [activeFilterChips, setActiveFilterChips] = useState<ActiveFilterChip[]>([])
  const [filterResetToken, setFilterResetToken] = useState(0)
  const [selectedFocusId, setSelectedFocusId] = useState<number>(0)
  const [selectedMarkerId, setSelectedMarkerId] = useState<number>(0)
  const [followVehicleId, setFollowVehicleId] = useState<number>(0)
  const [detailOpen, setDetailOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [viewTab, setViewTab] = useState<'map' | 'grid'>('map')
  const [mobileTab, setMobileTab] = useState<'list' | 'map'>('map')
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 992 : true
  )
  const [gridPage, setGridPage] = useState(1)
  const [gridPageSize, setGridPageSize] = useState(10)
  const [gridSearch, setGridSearch] = useState('')
  const [gridSortKey, setGridSortKey] = useState<SortKey>('jobCount')
  const [gridSortDir, setGridSortDir] = useState<SortDir>('desc')
  const [gridAllTime, setGridAllTime] = useState(false)
  const dispatch = useDispatch()

  const gridTimeRange: 'month' | 'all' = gridAllTime ? 'all' : 'month'

  const itemIdString = useMemo(
    () => (MapViewVehicle || []).map((id: number) => `=${id}`).join(','),
    [MapViewVehicle]
  )

  const requestBody = {
    statusCode: filterOptions.statusCode !== '' ? filterOptions.statusCode : null,
    fK_VehicleType: filterOptions.fK_VehicleType !== '' ? filterOptions.fK_VehicleType : null,
    fK_BaseLocation: filterOptions.fK_BaseLocation !== '' ? filterOptions.fK_BaseLocation : null,
    regNo: filterOptions.regNo?.length > 0 ? filterOptions.regNo : null,
    fK_VehicleFeature: null,
    fK_VehicleCode: null,
    CT_FK_OperatingTypeCode:
      filterOptions.cT_FK_OperatingTypeCode !== '' && filterOptions.cT_FK_OperatingTypeCode != null
        ? Number(filterOptions.cT_FK_OperatingTypeCode)
        : null,
    CT_FK_CustomerSpecificationCode:
      filterOptions.cT_FK_CustomerSpecificationCode !== '' &&
      filterOptions.cT_FK_CustomerSpecificationCode != null
        ? Number(filterOptions.cT_FK_CustomerSpecificationCode)
        : null,
  }

  const {isLoading: isAssignVehicleLoading, isFetching: isAssignVehicleFetching} =
    useAssignVehicleQuery(
      {comapny: company, requestBody},
      {
        refetchOnMountOrArgChange: true,
        pollingInterval: 0,
        skip: false,
      }
    )

  const {
    data: statsData,
    isLoading: isStatsLoading,
    isFetching: isStatsFetching,
  } = useAvailableVehicleStatisticsQuery(
    {
      company,
      requestBody,
      page: gridPage,
      itemsPerPage: gridPageSize,
      search: gridSearch,
      sortBy: gridSortKey,
      sortDir: gridSortDir,
      timeRange: gridTimeRange,
    },
    {
      skip: viewTab !== 'grid',
      refetchOnMountOrArgChange: true,
    }
  )

  const selectedPlate = useMemo(() => {
    const id = selectedMarkerId || selectedFocusId
    if (!id) return ''
    const vehicle = uniqueDataRedux.find((v: any) => (v?.itemID ?? v?.ItemID) === id)
    return String(vehicle?.registrationNo ?? vehicle?.RegistrationNo ?? '').trim()
  }, [selectedMarkerId, selectedFocusId, uniqueDataRedux])

  const {
    data: detailStatsData,
    isLoading: isDetailStatsLoading,
    isFetching: isDetailStatsFetching,
  } = useAvailableVehicleStatisticsQuery(
    {
      company,
      requestBody,
      page: 1,
      itemsPerPage: 1,
      search: selectedPlate,
      sortBy: 'regNo',
      sortDir: 'asc',
      timeRange: gridTimeRange,
    },
    {
      skip: viewTab !== 'map' || !detailOpen || !selectedPlate,
      refetchOnMountOrArgChange: true,
    }
  )

  const detailStatsLoading =
    !!selectedPlate &&
    detailOpen &&
    viewTab === 'map' &&
    (isDetailStatsLoading || isDetailStatsFetching)

  const {
    data: sid,
    error: sidError,
    refetch: refetchSID,
  } = useFetchSIDTokenQuery(
    {},
    {
      refetchOnMountOrArgChange: false,
      pollingInterval: 0,
      skip: MapViewVehicle.length === 0,
    }
  )

  const {
    data: gpsVehicleDetails,
    error: gpsVehicleDetailsError,
  } = useGetGPSVehicleDetailsQuery(sid ? {itemIdsArray: itemIdString, sID: sid} : skipToken, {
    refetchOnMountOrArgChange: true,
    pollingInterval: 6000,
    skip:
      !sid ||
      ActivePage !== '/assign-vehicle-view' ||
      MapViewVehicle.length === 0 ||
      !itemIdString ||
      itemIdString === '',
  })

  useEffect(() => {
    dispatch(resetState())
  }, [dispatch])

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 992)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    setGridPage(1)
  }, [filterOptions, gridSearch, gridPageSize, gridSortKey, gridSortDir, gridAllTime])

  useEffect(() => {
    if (ActivePage !== '/assign-vehicle-view') return

    if (MapViewVehicle.length === 0 || !itemIdString) {
      dispatch(setGPSVehiclesDetails([]))
      dispatch(setMarkers([]))
      return
    }

    if (gpsVehicleDetails) {
      dispatch(setGPSVehiclesDetails(gpsVehicleDetails))
      dispatch(setMarkers(gpsVehicleDetails))
    }

    if (gpsVehicleDetailsError || sidError) {
      refetchSID()
    }
  }, [
    ActivePage,
    MapViewVehicle.length,
    itemIdString,
    gpsVehicleDetails,
    gpsVehicleDetailsError,
    sidError,
    refetchSID,
    dispatch,
  ])

  const vehicleListData: AvailableVehicleListItem[] = useMemo(() => {
    return uniqueDataRedux.map((vehicle: any) => {
      const itemId = vehicle?.itemID ?? vehicle?.ItemID
      const vehicleNumber = vehicle?.registrationNo ?? vehicle?.RegistrationNo ?? ''
      const gpsItem = GPSVehiclesDetails?.find((item: any) => item?.id === itemId)
      return {
        itemId,
        vehicleNumber,
        vehicleType: vehicle?.vehicleType ?? vehicle?.VehicleType ?? null,
        baseLocation: vehicle?.baseLocation ?? vehicle?.BaseLocation ?? null,
        speed: gpsItem?.pos?.s != null ? gpsItem.pos.s : null,
        gpsStatus: resolveGpsStatus(gpsItem),
      }
    })
  }, [uniqueDataRedux, GPSVehiclesDetails])

  const searchData = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return vehicleListData
    return vehicleListData.filter((vehicle) => vehicle.vehicleNumber.toLowerCase().includes(q))
  }, [vehicleListData, searchQuery])

  const selectedDetailVehicle = useMemo(() => {
    const id = selectedMarkerId || selectedFocusId
    if (!id) return null
    const base = vehicleListData.find((v) => v.itemId === id)
    if (!base) return null
    const gpsItem = GPSVehiclesDetails?.find((item: any) => item?.id === id)
    const plate = String(base.vehicleNumber).toLowerCase()
    const statsRow = (detailStatsData?.items || []).find(
      (row: any) => String(row?.regNo ?? row?.RegNo ?? '').toLowerCase() === plate
    )
    return {
      ...base,
      lastUpdateUnix: gpsItem?.pos?.t ?? null,
      jobCount: statsRow?.jobCount ?? null,
      totalKM: statsRow?.totalKM ?? statsRow?.TotalKM ?? null,
      revenue: statsRow?.revenue ?? statsRow?.Revenue ?? null,
    }
  }, [
    selectedMarkerId,
    selectedFocusId,
    vehicleListData,
    GPSVehiclesDetails,
    detailStatsData,
  ])

  const selectVehicle = (itemId: number) => {
    setSelectedMarkerId(itemId)
    setSelectedFocusId(itemId)
    setDetailOpen(true)
    if (followVehicleId && followVehicleId !== itemId) {
      setFollowVehicleId(itemId)
    }
    if (!isDesktop) setMobileTab('map')
  }

  const closeDetailPanel = () => {
    setDetailOpen(false)
  }

  const clearSelection = () => {
    setSelectedMarkerId(0)
    setSelectedFocusId(0)
    setFollowVehicleId(0)
    setDetailOpen(false)
  }

  const showAllOnMap = useCallback(() => {
    const ids = searchData.map((v) => v.itemId)
    dispatch(setMapViewVehicle(ids))
  }, [searchData, dispatch])

  const toggleFollow = () => {
    const target = followVehicleId || selectedMarkerId || selectedFocusId
    if (!target) return
    if (followVehicleId === target) {
      setFollowVehicleId(0)
    } else {
      setFollowVehicleId(target)
      setSelectedFocusId(target)
      setSelectedMarkerId(target)
      setDetailOpen(true)
    }
  }

  const handleGridSearchChange = useCallback((value: string) => {
    setGridSearch(value)
  }, [])

  const handleGridSortChange = useCallback((key: SortKey, dir: SortDir) => {
    setGridSortKey(key)
    setGridSortDir(dir)
  }, [])

  const handleGridAllTimeChange = useCallback((value: boolean) => {
    setGridAllTime(value)
  }, [])

  const layoutCSS = {
    height: '100%',
    overflow: 'hidden' as const,
    minWidth: '100px',
    background: '#e4e6ef',
  }

  const listPane = (
    <AvailableVehicleList
      vehicles={searchData}
      mapViewVehicle={MapViewVehicle}
      selectedFocusId={selectedFocusId}
      selectedMarkerId={selectedMarkerId}
      searchQuery={searchQuery}
      isLoading={isAssignVehicleLoading || isAssignVehicleFetching}
      showingOnMapCount={MapViewVehicle.length}
      totalCount={vehicleListData.length}
      onSearchChange={setSearchQuery}
      onToggleVehicle={(itemId) => {
        if (MapViewVehicle.includes(itemId)) {
          dispatch(setMapViewVehicle(MapViewVehicle.filter((code: number) => code !== itemId)))
        } else {
          dispatch(setMapViewVehicle([...MapViewVehicle, itemId]))
        }
      }}
      onToggleAll={() => {
        const filteredIds = searchData.map((v) => v.itemId)
        const allSelected =
          filteredIds.length > 0 && filteredIds.every((id) => MapViewVehicle.includes(id))
        if (allSelected) {
          dispatch(
            setMapViewVehicle(MapViewVehicle.filter((id: number) => !filteredIds.includes(id)))
          )
        } else {
          const merged = Array.from(new Set([...MapViewVehicle, ...filteredIds]))
          dispatch(setMapViewVehicle(merged))
        }
      }}
      onSelectVehicle={selectVehicle}
      onToggleFocus={(itemId) => {
        if (selectedFocusId === itemId) {
          setSelectedFocusId(0)
          if (followVehicleId === itemId) setFollowVehicleId(0)
        } else {
          selectVehicle(itemId)
        }
      }}
    />
  )

  const mapPane = (
    <div className='vm-map-pane position-relative h-100'>
      <MapComponent
        selectedFocusId={selectedFocusId}
        selectedMarkerId={selectedMarkerId}
        setSelectedMarkerId={(id) => {
          if (id) {
            selectVehicle(id)
          } else {
            setSelectedMarkerId(0)
          }
        }}
        followVehicleId={followVehicleId}
        showLegend
        showMapToolbar
        labelMode='zoomOrSelected'
        mapContainerHeight='100%'
        onClearSelection={clearSelection}
        onToggleFollow={toggleFollow}
        onShowAll={showAllOnMap}
        onMarkerSelect={(id) => {
          if (id) selectVehicle(id)
        }}
        infoVehicle={
          selectedDetailVehicle
            ? {
                vehicleNumber: selectedDetailVehicle.vehicleNumber,
                vehicleType: selectedDetailVehicle.vehicleType,
                baseLocation: selectedDetailVehicle.baseLocation,
              }
            : null
        }
      />
      <AvailableVehicleDetailPanel
        open={detailOpen && !!selectedDetailVehicle}
        variant={isDesktop ? 'drawer' : 'sheet'}
        vehicle={selectedDetailVehicle}
        statsLoading={detailStatsLoading}
        isFollowing={!!followVehicleId && followVehicleId === selectedDetailVehicle?.itemId}
        onClose={closeDetailPanel}
        onFocus={() => {
          if (selectedDetailVehicle) {
            setSelectedFocusId(selectedDetailVehicle.itemId)
            setSelectedMarkerId(selectedDetailVehicle.itemId)
          }
        }}
        onToggleFollow={toggleFollow}
      />
    </div>
  )

  return (
    <div className='card vm-monitoring-card'>
      <div className='card-body p-0'>
        <div className='d-flex justify-content-between flex-wrap'>
          <div className='accordion flex-grow-1' id='kt_accordion_1'>
            <div className='accordion-item px-5 py-3 border-0'>
              <h3 className='accordion-header py-0' id='kt_accordion_1_header_1'>
                <button
                  className='accordion-button fw-semibold collapsed text-gray-400'
                  style={{
                    paddingTop: '.65rem',
                    paddingBottom: '.65rem',
                    fontSize: '1rem',
                  }}
                  type='button'
                  data-bs-toggle='collapse'
                  data-bs-target='#kt_accordion_1_body_1'
                  aria-expanded='false'
                  aria-controls='kt_accordion_1_body_1'
                >
                  <i className='bi bi-sliders me-2'></i>
                  Advance Search
                </button>
              </h3>
              <AdvanceSearch
                setFilterOptions={setFilterOptions}
                onActiveFiltersChange={setActiveFilterChips}
                resetToken={filterResetToken}
              />
              {activeFilterChips.length > 0 && (
                <div className='vm-filter-chips px-1 pt-2'>
                  {activeFilterChips.map((chip) => (
                    <span
                      key={chip.id}
                      className='vm-filter-chip'
                      title={`${chip.label}: ${chip.value}`}
                    >
                      <strong>{chip.label}:</strong> {chip.value}
                    </span>
                  ))}
                  <button
                    type='button'
                    className='btn btn-sm btn-light-primary vm-filter-chip-clear'
                    onClick={() => setFilterResetToken((t) => t + 1)}
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className='separator'></div>

        <div className='av-view-tabs'>
          <button
            type='button'
            className={`av-view-tab ${viewTab === 'map' ? 'is-active' : ''}`}
            onClick={() => setViewTab('map')}
          >
            <KTSVG path='/media/icons/duotune/maps/map003.svg' className='svg-icon-muted me-2' />
            Map
          </button>
          <button
            type='button'
            className={`av-view-tab ${viewTab === 'grid' ? 'is-active' : ''}`}
            onClick={() => setViewTab('grid')}
          >
            <KTSVG
              path='/media/icons/duotune/layouts/lay002.svg'
              className='svg-icon-muted me-2'
            />
            Grid View
          </button>
        </div>

        {viewTab === 'map' && !isDesktop && (
          <div className='vm-mobile-tabs'>
            <button
              type='button'
              className={`vm-mobile-tab ${mobileTab === 'list' ? 'is-active' : ''}`}
              onClick={() => setMobileTab('list')}
            >
              List
            </button>
            <button
              type='button'
              className={`vm-mobile-tab ${mobileTab === 'map' ? 'is-active' : ''}`}
              onClick={() => setMobileTab('map')}
            >
              Map
            </button>
            <span className='vm-mobile-tabs__count'>
              {MapViewVehicle.length}/{vehicleListData.length}
            </span>
          </div>
        )}

        <div className='vm-main-stage'>
          {viewTab === 'map' ? (
            isDesktop ? (
              <SplitPane
                split='vertical'
                sizes={sizes}
                onChange={(nextSizes) => setSizes(nextSizes)}
                sashRender={() => <div className='sash' />}
              >
                <Pane minSize={180} maxSize='45%'>
                  <div style={layoutCSS}>{listPane}</div>
                </Pane>
                <div style={{height: '100%'}}>{mapPane}</div>
              </SplitPane>
            ) : (
              <div className='vm-mobile-stage h-100'>
                {mobileTab === 'list' ? (
                  <div className='h-100' style={{background: '#e4e6ef'}}>
                    {listPane}
                  </div>
                ) : (
                  mapPane
                )}
              </div>
            )
          ) : (
            <div className='av-grid-stage'>
              <AvailableVehicleGrid
                rows={statsData?.items || []}
                totalCount={statsData?.totalCount ?? 0}
                page={gridPage}
                pageSize={gridPageSize}
                search={gridSearch}
                sortKey={gridSortKey}
                sortDir={gridSortDir}
                allTime={gridAllTime}
                isLoading={isStatsLoading || isStatsFetching}
                onPageChange={setGridPage}
                onPageSizeChange={setGridPageSize}
                onSearchChange={handleGridSearchChange}
                onSortChange={handleGridSortChange}
                onAllTimeChange={handleGridAllTimeChange}
              />
            </div>
          )}
        </div>

        {(isAssignVehicleLoading || isAssignVehicleFetching) && viewTab === 'map' && <Loading />}
      </div>
    </div>
  )
}

export {AssignVehicleView}
